import React, { memo, useMemo } from 'react';

import { AR_LANE_TOP_Y } from '../../lib/percusionCorporal';

import {

  buildLaneLayouts,

  vanishPointX,

  vanishPointY,

  type LaneLayout,

} from '../../lib/percusionPerspective';

import type { PercusionBucketRect } from '../../lib/percusionCorporal';

import type { RhythmBubble } from '../../lib/game/atrapaRitmoEngine';



interface AtrapaRitmoArenaProps {

  width: number;

  playfieldHeight: number;

  horizonY: number;

  bucketRects: PercusionBucketRect[];

  laneLayouts: LaneLayout[];

  bubbles: RhythmBubble[];

  bucketFlashUntil: Record<number, number>;

  feedback: 'hit' | 'miss' | 'combo' | null;

  layer: 'static' | 'dynamic';

  flashNow?: number;

}



/** Capa estática — carriles cristal esmerilado + línea de meta. */

export const AtrapaRitmoArenaStatic = memo(function AtrapaRitmoArenaStatic({

  width,

  playfieldHeight,

  horizonY,

  laneLayouts,

  bucketFlashUntil,

  flashNow = 0,

}: Omit<AtrapaRitmoArenaProps, 'layer' | 'bubbles' | 'feedback' | 'bucketRects'> & {

  bucketRects?: PercusionBucketRect[];

}) {

  const vanishX = vanishPointX({ width, horizonY });

  const vanishY = vanishPointY({ width, horizonY });



  return (

    <svg

      width={width}

      height={playfieldHeight}

      viewBox={`0 0 ${width} ${playfieldHeight}`}

      className="absolute inset-0 w-full h-full"

      aria-hidden

    >

      {laneLayouts.map((layout) => {

        const flashed = (bucketFlashUntil[layout.lane] ?? 0) > flashNow;

        const xBL = layout.bottomX - layout.bottomHalfW;

        const xBR = layout.bottomX + layout.bottomHalfW;

        const xTL = layout.xLeft(AR_LANE_TOP_Y);

        const xTR = layout.xRight(AR_LANE_TOP_Y);



        return (

          <path

            key={layout.lane}

            d={`M ${xTL} ${AR_LANE_TOP_Y} L ${xTR} ${AR_LANE_TOP_Y} L ${xBR} ${horizonY} L ${xBL} ${horizonY} Z`}

            fill={layout.laneTint}

            opacity={flashed ? 0.95 : 0.72}

          />

        );

      })}



      <circle cx={vanishX} cy={vanishY} r={3} fill="#CBD5E1" opacity={0.6} />



      <line

        x1={16}

        y1={horizonY}

        x2={width - 16}

        y2={horizonY}

        stroke="#E2E8F0"

        strokeWidth={2}

        strokeLinecap="round"

      />

    </svg>

  );

});



/** Capa dinámica — feedback de combo. */

export const AtrapaRitmoArenaDynamic = memo(function AtrapaRitmoArenaDynamic({

  width,

  horizonY,

  feedback,

}: Pick<

  AtrapaRitmoArenaProps,

  'width' | 'playfieldHeight' | 'horizonY' | 'laneLayouts' | 'bubbles' | 'feedback'

>) {

  if (feedback !== 'combo') return null;



  return (

    <svg

      width={width}

      height={horizonY}

      viewBox={`0 0 ${width} ${horizonY}`}

      className="absolute inset-0 w-full h-full pointer-events-none"

      aria-hidden

    >

      <text

        x={width / 2}

        y={horizonY * 0.42}

        textAnchor="middle"

        fontSize={28}

        fontFamily="Fredoka, Nunito, sans-serif"

        fontWeight={500}

        fill="#818CF8"

      >

        ¡Combo!

      </text>

    </svg>

  );

});



/** @deprecated use Static + Dynamic split */

export const AtrapaRitmoArena = memo(function AtrapaRitmoArena(props: AtrapaRitmoArenaProps) {

  const laneLayouts = useMemo(

    () =>

      props.laneLayouts.length

        ? props.laneLayouts

        : buildLaneLayouts(props.bucketRects, { width: props.width, horizonY: props.horizonY }),

    [props.laneLayouts, props.bucketRects, props.width, props.horizonY]

  );



  if (props.layer === 'static') {

    return (

      <AtrapaRitmoArenaStatic

        width={props.width}

        playfieldHeight={props.playfieldHeight}

        horizonY={props.horizonY}

        bucketRects={props.bucketRects}

        laneLayouts={laneLayouts}

        bucketFlashUntil={props.bucketFlashUntil}

        flashNow={props.flashNow}

      />

    );

  }



  return (

    <AtrapaRitmoArenaDynamic

      width={props.width}

      playfieldHeight={props.playfieldHeight}

      horizonY={props.horizonY}

      laneLayouts={laneLayouts}

      bubbles={props.bubbles}

      feedback={props.feedback}

    />

  );

});



export default AtrapaRitmoArena;

